import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Container, Paper, Button, Link } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from '../../theme/theme';
import AuthLayout from './AuthLayout';
import { useForgotPassword } from '../Query/ForgotPasswordQuery';
import AllForm from './Form';
import * as Yup from 'yup';
import { useSnackbar } from './SnackBarProvider';
import ConfirmationDialog from './ConfirmationDialog';
import { COLORS } from '../../theme';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(false);
  const { mutate: ForgotPasswordMutation, isPending: ForgotPasswordLoading } =
    useForgotPassword();
  const { showMessage } = useSnackbar();

  const location = useLocation();
  const previousHash = useRef(window.location.hash);
  const newHash = useRef<string | null>(null);
  const channel = new URLSearchParams(location.search).get('channel');

  useEffect(() => {
    const handleHashChange = (event: HashChangeEvent) => {
      event.preventDefault();
      newHash.current = window.location.hash;
      window.location.hash = previousHash.current;
      setShowConfirmDialog(true);
      setPendingNavigation(false);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const initialValues = {
    email: '',
  };

  const validationSchema = Yup.object().shape({
    email: channel === 'email' 
      ? Yup.string()
          .email('Please enter a valid email address')
          .required('Email is required')
      : Yup.string().notRequired(),
    phone: channel === 'phone'
      ? Yup.string()
          .matches(/^\+[1-9]{1,3}\d{7,10}$/, 'Please enter a valid phone number')
          .required('Mobile Number is required')
      : Yup.string().notRequired(),
  }) as any;

  const fields = [
    channel === 'email' ?
      {
        name: 'email',
        label: 'Email',
        type: 'text',
        isRequired: true,
      } : {
        name: 'phone',
        label: 'Mobile Number',
        type: 'text',
        isRequired: true,
      },
  ];

  const onSubmit = async (values: Record<string, any>) => {
    const emailOrMobile = channel === 'email' ? { email: values.email } : { phoneNumber: values.phone }
    const modifiedValues = {
      ...emailOrMobile,
      platformType: 'admin',
      channel: channel,
    };

    try {
      await ForgotPasswordMutation(modifiedValues, {
        onSuccess: () => {
          channel==='email'?
          navigate(`/reset-password?channel=${encodeURIComponent(channel)}&email=${encodeURIComponent(values.email)}`):navigate(`/reset-password?channel=${channel || 'phone'}&phone=${encodeURIComponent(values.phone)}`);
        },
        onError: (error: any) => {
          console.log(error);
        },
      });
    } catch (error: any) {
      console.log(error, 'error');
      showMessage(error.message || 'An unexpected error occurred.', 'error');
    }
  };

  const ForgotPasswordForm = AllForm({
    initialValues,
    validationSchema,
    onSubmit,
    fields,
    customButton: (formikProps: any) => (
      // <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      //   <Button
      //     type="submit"
      //     style={{
      //       width: '50%',
      //       backgroundColor: 'rgb(76, 10, 39)',
      //       color: '#fff',
      //       border: 'none',
      //       borderRadius: 6,
      //       fontSize: 16,
      //       fontWeight: 600,
      //       cursor: 'pointer',
      //       transition: 'background 0.3s',
      //       padding: '15px 50px',
      //     }}
      //     disabled={formikProps.isSubmitting}
      //   >
      //     {ForgotPasswordLoading ? 'Sending...' : 'Reset password'}
      //   </Button>
      // </div>
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Button
          type="submit"
          style={{
            backgroundColor: COLORS.primary.main,
            color: COLORS.secondary.main,
            border: 'none',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.3s',
            padding: '10px 20px', // Reduced padding
            whiteSpace: 'nowrap', // Prevent text wrap
          }}
          disabled={formikProps.isSubmitting}
        >
          {ForgotPasswordLoading ? 'Sending...' : 'Reset password'}
        </Button>
      </div>
    ),
  });

  const handleBackToLogin = () => {
    setShowConfirmDialog(true);
    setPendingNavigation(true);
  };

  const handleConfirmNavigation = () => {
    setShowConfirmDialog(false);
    if (pendingNavigation && newHash.current) {
      window.location.hash = '#/';
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleCancelNavigation = () => {
    setShowConfirmDialog(false);
    setPendingNavigation(false);
    newHash.current = null;
    window.history.pushState(null, '', window.location.href);
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout>
        <Container maxWidth="md">
          <Paper
            elevation={3}
            sx={{ p: 5, borderRadius: 3, textAlign: 'center' }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <span
                  role="img"
                  aria-label="fingerprint"
                  style={{ fontSize: 28 }}
                >
                  🔒
                </span>
              </Box>
            </Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, mb: 1, fontSize: 16 }}
            >
              Forgot password?
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: '#888', mb: 3, fontSize: 16 }}
            >
              No worries, we'll send you reset instructions.
            </Typography>

            {ForgotPasswordForm()}

            <Link
              component="button"
              onClick={handleBackToLogin}
              sx={{
                color: '#888',
                fontSize: 16,
                mt: 1,
                textDecoration: 'none',
                fontFamily: 'Livvic',
              }}
            >
              ← Back to log in
            </Link>

            <ConfirmationDialog
              open={showConfirmDialog}
              title="Confirm Navigation"
              message="Are you sure you want to exit? Any unsaved changes will be lost."
              onConfirm={handleConfirmNavigation}
              onCancel={handleCancelNavigation}
            />
          </Paper>
        </Container>
      </AuthLayout>
    </ThemeProvider>
  );
};

export default ForgotPassword;
